import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import type { IBiometricAuthPort } from '@app/domain/ports';

/**
 * Server/alias used to namespace the stored credential in the Android Keystore.
 * On Android, NativeBiometric stores credentials encrypted with an AES key
 * that is backed by the Android Keystore.  On iOS it uses the Keychain.
 *
 * The synthetic PRF bytes are stored as the `password` field (base64-encoded);
 * the `username` field carries the user `id` passed from the use-case layer.
 */
const KEYSTORE_SERVER = 'personal-finance-dek-biometric';

function uint8ArrayToBase64(bytes: Uint8Array): string {
  return btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(''));
}

/**
 * Decodes a Base64 string to Uint8Array.
 * Throws a `DOMException` (from `atob`) if the input is not valid Base64; this
 * propagates naturally to the calling use-case's try/catch (e.g.
 * `UnlockDekWithWebAuthnUseCase`) so the caller can return a typed failure
 * result without an additional wrapper here.
 */
function base64ToUint8Array(base64: string): Uint8Array {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

/**
 * Capacitor biometric adapter that implements {@link IBiometricAuthPort} as a
 * drop-in replacement for {@link WebAuthnAdapter} on the Capacitor runtime.
 *
 * Uses the `@capgo/capacitor-native-biometric` plugin — no custom native code is
 * required. The plugin handles:
 *   - BiometricPrompt (Android) / LAContext (iOS) for user verification.
 *   - Credential storage encrypted with an Android Keystore key (Android) or
 *     Keychain item (iOS).
 *
 * Security model:
 *   `verifyIdentity()` → success → retrieve Keystore-protected PRF bytes
 *   → HKDF → KEK → unwrap → DEK → decrypt app data
 *
 * Note: This is a "verify-then-read" pattern rather than a CryptoObject-bound
 * operation. The biometric check and the key retrieval are two separate OS
 * calls. This is the same model used by most mobile banking apps and is
 * appropriate for offline-first personal finance data.
 *
 * The existing use cases (`ActivateWebAuthnUseCase`, `UnlockDekWithWebAuthnUseCase`)
 * work unchanged because they depend only on `IBiometricAuthPort`.
 * The DI container selects this adapter when `RUNTIME_KIND === 'capacitor'`.
 */
export class AndroidBiometricAdapter implements IBiometricAuthPort {
  constructor(private readonly translate: (key: string) => string) {}

  async enrollBiometric(
    id: string,
    // The following parameters are required by IBiometricAuthPort for the WebAuthn
    // PRF flow (user profile for passkey creation, salt for PRF evaluation).
    // Here we only use `id` as the credential username; the rest are not needed.
    _name: string,
    _displayName: string,
    _salt: string,
  ): Promise<{ credentialId: string; prfOutput: Uint8Array }> {
    // 1. Verify biometric before generating and storing new key material.
    await NativeBiometric.verifyIdentity({
      title: this.translate('common.biometric_prompt.setup_title'),
      subtitle: this.translate('common.biometric_prompt.setup_subtitle'),
      description: this.translate('common.biometric_prompt.description'),
      negativeButtonText: this.translate('common.actions.cancel'),
    });

    // 2. Generate cryptographically random PRF-equivalent bytes.
    const prfOutput = crypto.getRandomValues(new Uint8Array(32));

    // 3. Store the PRF bytes (base64) in the Android Keystore / iOS Keychain.
    await NativeBiometric.setCredentials({
      username: id,
      password: uint8ArrayToBase64(prfOutput),
      server: KEYSTORE_SERVER,
    });

    return { credentialId: id, prfOutput };
  }

  async deriveKeyFromBiometric(
    // `credentialId` identifies the stored credential (username key in Keystore).
    // The current NativeBiometric plugin returns the single credential stored per
    // server — filtering by username is not needed for a single-user device app.
    _credentialId: string,
    // `salt` is the WebAuthn PRF evaluation seed — not applicable here.
    _salt: string,
  ): Promise<Uint8Array> {
    // 1. Require biometric verification before accessing stored key material.
    await NativeBiometric.verifyIdentity({
      title: this.translate('common.biometric_prompt.unlock_title'),
      subtitle: this.translate('common.biometric_prompt.unlock_subtitle'),
      description: this.translate('common.biometric_prompt.description'),
      negativeButtonText: this.translate('common.actions.cancel'),
    });

    // 2. Retrieve the PRF bytes that were stored during enrollment.
    const credentials = await NativeBiometric.getCredentials({
      server: KEYSTORE_SERVER,
    });

    return base64ToUint8Array(credentials.password);
  }
}
